import { Box, Typography } from '@mui/material';
import React, { useCallback } from 'react';
import ContentManagementInterface from '../../models/ContentManagementInterface';
import { Palette } from 'Palette';
import ImageCarousel from '../template/ImageCarousel';
import LeftRightList from '../template/LeftRightList';
import StandardHeaderText from '../template/StandardHeaderText';
import Subtitle from '../template/Subtitle';
import Table from '../template/Table';
import TextBlock from '../template/TextBlock';
import HeroImage from '../template/HeroImage';
import { ContentDiffComparisonProps } from './contentDiffComparisonTypes';

export function useDefaultRenderContentItem(tone: keyof typeof Palette) {
  return useCallback(
    (item: any, modelId: string, isOriginal: boolean) => {
      const key = `${item.id}-${isOriginal ? 'original' : 'new'}`;

      switch (item.type) {
        case 'StandardHeaderText':
          return (
            <StandardHeaderText
              key={key}
              {...item.properties}
              id={key}
              editMode={false}
              tone={tone}
            />
          );

        case 'Subtitle':
          return <Subtitle key={key} {...item.properties} id={key} tone={tone} />;

        case 'Text':
          return <TextBlock key={key} {...item.properties} id={key} tone={tone} />;

        case 'HeroImage':
          return <HeroImage key={key} {...item.properties} id={key} tone={tone} />;

        case 'ImageCarousel':
          return (
            <ImageCarousel
              key={key}
              {...item.properties}
              id={key}
              modelId={modelId}
              modelType="FundOpportunity"
              editMode={false}
              tone={tone}
              forceExpanded={true}
            />
          );

        case 'LeftRightList':
          return (
            <LeftRightList key={key} {...item.properties} id={key} editMode={false} tone={tone} />
          );

        case 'Table':
          return <Table key={key} {...item.properties} id={key} editMode={false} tone={tone} />;

        default:
          return <Typography color="text.secondary">Unknown content type: {item.type}</Typography>;
      }
    },
    [tone],
  );
}

export function useRenderContentReadOnly(
  renderContentItem: ContentDiffComparisonProps['renderContentItem'],
  defaultRenderContentItem: ReturnType<typeof useDefaultRenderContentItem>,
) {
  return useCallback(
    (
      content: ContentManagementInterface,
      isOriginal: boolean,
      compareContent?: ContentManagementInterface,
      isCompareOriginal?: boolean,
    ) => {
      const renderItem = renderContentItem || defaultRenderContentItem;

      return (
        <Box>
          {content.content.map((item) => (
            <Box
              key={`${item.id}-${isOriginal ? 'original' : 'new'}`}
              className="content-management-item"
              sx={{ mb: 3 }}
            >
              {renderItem(
                item,
                content.relatedObjectId,
                isOriginal,
                compareContent,
                isCompareOriginal,
              )}
            </Box>
          ))}
        </Box>
      );
    },
    [renderContentItem, defaultRenderContentItem],
  );
}
